import { z } from 'zod';
import { UserSchema } from './auth.js';

/** User profile (self-serve; user_profiles table) and sign-in history. */

export const WorkingPatternSchema = z.enum(['', 'remote', 'hybrid', 'office']);
export type WorkingPattern = z.infer<typeof WorkingPatternSchema>;

export const UserProfileSchema = z.object({
  jobTitle: z.string(),
  phone: z.string(),
  addressLine1: z.string(),
  addressLine2: z.string(),
  city: z.string(),
  region: z.string(),
  postcode: z.string(),
  country: z.string(),
  workingPattern: WorkingPatternSchema,
  hasAvatar: z.boolean(),
  updatedAt: z.string().nullable(),
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

export const UpdateUserProfileInputSchema = z.object({
  /** Display name (updates the user record, shown across the app). */
  name: z.string().min(1).max(200).optional(),
  jobTitle: z.string().max(200).optional(),
  phone: z.string().max(50).optional(),
  addressLine1: z.string().max(200).optional(),
  addressLine2: z.string().max(200).optional(),
  city: z.string().max(100).optional(),
  region: z.string().max(100).optional(),
  postcode: z.string().max(20).optional(),
  country: z.string().max(100).optional(),
  workingPattern: WorkingPatternSchema.optional(),
});
export type UpdateUserProfileInput = z.infer<typeof UpdateUserProfileInputSchema>;

/** Where a sign-in came from, detected at login time. */
export const LoginClientSchema = z.enum(['web', 'mobile', 'desktop', 'unknown']);
export type LoginClient = z.infer<typeof LoginClientSchema>;

export const LoginEventSchema = z.object({
  id: z.string(),
  client: LoginClientSchema,
  userAgent: z.string(),
  ip: z.string().nullable(),
  createdAt: z.string(),
});
export type LoginEvent = z.infer<typeof LoginEventSchema>;

export const ProfileResponseSchema = z.object({
  user: UserSchema,
  profile: UserProfileSchema,
  /** Distinct clients this user has ever signed in from. */
  clientsUsed: z.array(LoginClientSchema),
  /** Most recent sign-ins, newest first. */
  recentLogins: z.array(LoginEventSchema),
});
export type ProfileResponse = z.infer<typeof ProfileResponseSchema>;
